import { useCallback, useEffect, useState } from "react";
import { SignIn, SignUp, useAuth, useClerk, useUser } from "@clerk/clerk-react";

import {
  ApiError,
  approvePairing,
  denyPairing,
  getPairing,
  type Pairing,
} from "./api";
import regiooWhiteLogo from "./assets/regioo-white.png";
import {
  CHANNEL_OPTIONS,
  PROFILE_QUESTION,
  isPreferredChannelKey,
  needsAsking,
  readProfile,
  type PreferredChannelKey,
} from "./profile";

/**
 * Second screen of the TV sign-in flow.
 *
 * The division of labour is the point of the whole design: **Clerk** authenticates the viewer
 * (password, email code, social, MFA — whatever the instance is configured for, all inside its own
 * <SignIn /> UI), and this page does nothing but carry the code from the television to the backend
 * along with the resulting session token. It never sees a credential, and it never talks to
 * Clerk's Backend API.
 */

type Outcome =
  | { kind: "approved"; deviceName: string | null }
  | { kind: "denied" }
  | { kind: "error"; message: string };

/** Reads the code the QR link pre-filled. Kept in the URL so it survives Clerk's sign-in redirect. */
function codeFromUrl(): string {
  return new URLSearchParams(window.location.search).get("code")?.trim() ?? "";
}

/**
 * Rebuilds this page's own URL, preserving the pairing code and dropping the auth mode.
 *
 * Sign-up lives on this page behind `?mode=signup` rather than at Clerk's hosted Account Portal,
 * for two reasons. Clerk's `localization` prop covers Clerk COMPONENTS only — the Account Portal is
 * served from Clerk's own domain and stays English however it is configured here, so a viewer who
 * tapped "Registreren" would have landed on an English page. And leaving this origin mid-pairing
 * puts the `?code=` at the mercy of the redirect chain; keeping both flows here means it never
 * leaves the URL.
 */
function pageUrl(mode?: "signup"): string {
  const params = new URLSearchParams(window.location.search);
  params.delete("mode");
  if (mode) {
    params.set("mode", mode);
  }
  const query = params.toString();
  return `${window.location.origin}${window.location.pathname}${query ? `?${query}` : ""}`;
}

function modeFromUrl(): "signup" | "signin" {
  return new URLSearchParams(window.location.search).get("mode") === "signup"
    ? "signup"
    : "signin";
}

export default function App() {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const [userCode] = useState(codeFromUrl);
  const [editingProfileFor, setEditingProfileFor] = useState<string | null>(
    null,
  );
  const isEditingProfile = Boolean(userId) && editingProfileFor === userId;

  return (
    <div className="page-shell">
      <header className="site-header">
        <div className="site-logo-frame">
          <img className="site-logo" src={regiooWhiteLogo} alt="REGIOO" />
        </div>
        {isLoaded && isSignedIn && (
          <AccountStatus
            onEditProfile={() => {
              if (userId) {
                setEditingProfileFor(userId);
              }
            }}
            onSignedOut={() => setEditingProfileFor(null)}
          />
        )}
      </header>

      <div className="page-content">
        {!isLoaded ? (
          <AuthSkeleton />
        ) : !isSignedIn ? (
          <AuthStep />
        ) : isEditingProfile ? (
          <ProfileStep mode="edit" onDone={() => setEditingProfileFor(null)} />
        ) : (
          <SignedIn userCode={userCode} />
        )}
      </div>
    </div>
  );
}

/** Holds Clerk's place while it restores the browser session, avoiding a blank or flashing card. */
function AuthSkeleton() {
  return (
    <main className="auth-skeleton" aria-busy="true">
      <span className="sr-only" role="status">
        Inlogformulier laden…
      </span>
      <div className="skeleton skeleton-title" aria-hidden="true" />
      <div className="skeleton skeleton-subtitle" aria-hidden="true" />
      <div className="skeleton skeleton-provider" aria-hidden="true" />
      <div className="skeleton-divider" aria-hidden="true">
        <span />
        <i />
        <span />
      </div>
      <div className="skeleton skeleton-label" aria-hidden="true" />
      <div className="skeleton skeleton-field" aria-hidden="true" />
      <div className="skeleton skeleton-submit" aria-hidden="true" />
      <div className="skeleton skeleton-footer" aria-hidden="true" />
    </main>
  );
}

/** Persistent session context: viewers can see which Clerk account is active and leave it. */
function AccountStatus({
  onEditProfile,
  onSignedOut,
}: {
  onEditProfile: () => void;
  onSignedOut: () => void;
}) {
  const { signOut } = useClerk();
  const { isLoaded, user } = useUser();
  const [busy, setBusy] = useState(false);

  if (!isLoaded || !user) {
    return null;
  }

  const identity =
    user.primaryEmailAddress?.emailAddress ??
    user.fullName ??
    user.username ??
    "Je account";

  return (
    <aside className="account-status" aria-label="Actief account">
      <span className="account-avatar" aria-hidden="true">
        {identity.charAt(0).toLocaleUpperCase("nl-BE")}
      </span>
      <span className="account-copy">
        <span className="account-label">Aangemeld als</span>
        <strong className="account-identity" title={identity}>
          {identity}
        </strong>
      </span>
      <span className="account-actions">
        <button
          className="account-button"
          type="button"
          disabled={busy}
          onClick={onEditProfile}
        >
          Gegevens wijzigen
        </button>
        <button
          className="account-button"
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void signOut().then(onSignedOut, () => setBusy(false));
          }}
        >
          {busy ? "Afmelden…" : "Afmelden"}
        </button>
      </span>
    </aside>
  );
}

/**
 * Once signed in there may be one question left before confirming the pairing. It sits here rather
 * than earlier because this is the only screen in the whole flow with a keyboard — the television
 * has none, which is the reason this page exists at all.
 */
function SignedIn({ userCode }: { userCode: string }) {
  const { isLoaded, user } = useUser();
  const [answered, setAnswered] = useState(false);

  if (!isLoaded || !user) {
    return <Card busy note="Even geduld…" />;
  }

  if (!answered && needsAsking(user.unsafeMetadata)) {
    return <ProfileStep onDone={() => setAnswered(true)} />;
  }

  return userCode ? <ConfirmStep userCode={userCode} /> : <CodeForm />;
}

/**
 * Asks for the profile fields Clerk's own sign-up form cannot carry (see profile.ts), then hands
 * straight on to the pairing confirmation. Both fields are required: the postcode is retained as
 * profile information, while the station is a separate, explicit viewer choice. The same form
 * doubles as an explicit profile editor.
 */
function ProfileStep({
  onDone,
  mode = "onboarding",
}: {
  onDone: () => void;
  mode?: "onboarding" | "edit";
}) {
  const { user } = useUser();
  const savedProfile = readProfile(user?.unsafeMetadata);
  const [postcode, setPostcode] = useState(() => savedProfile.postcode ?? "");
  const [preferredChannel, setPreferredChannel] = useState<
    PreferredChannelKey | ""
  >(() => savedProfile.preferredChannel ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useCallback(async () => {
    if (!user) {
      setError("Je account kon niet worden geladen. Probeer het opnieuw.");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await user.update({
        unsafeMetadata: {
          ...(user.unsafeMetadata ?? {}),
          postcode: postcode.trim(),
          preferredChannel,
          profileCompletedAt: new Date().toISOString(),
        },
      });
      onDone();
    } catch {
      setError(
        mode === "edit"
          ? "We konden je voorkeuren niet wijzigen. Probeer het opnieuw."
          : "We konden je voorkeuren niet opslaan. Probeer het opnieuw.",
      );
      setBusy(false);
    }
  }, [mode, onDone, postcode, preferredChannel, user]);

  return (
    <main className="card">
      <h1>{mode === "edit" ? "Gegevens wijzigen" : "Nog één ding"}</h1>
      <p>
        {mode === "edit"
          ? "Wijzig je postcode of kies een andere voorkeurszender."
          : PROFILE_QUESTION.hint}
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!PROFILE_QUESTION.isValid(postcode)) {
            setError(PROFILE_QUESTION.invalidMessage);
            return;
          }
          if (!preferredChannel) {
            setError("Kies je voorkeurszender.");
            return;
          }
          void save();
        }}
      >
        <label htmlFor="postcode">{PROFILE_QUESTION.label}</label>
        <input
          id="postcode"
          className="field"
          value={postcode}
          onChange={(event) => {
            setPostcode(event.target.value);
            setError(null);
          }}
          inputMode={PROFILE_QUESTION.inputMode}
          maxLength={PROFILE_QUESTION.maxLength}
          placeholder={PROFILE_QUESTION.placeholder}
          autoComplete="postal-code"
          required
          disabled={busy}
        />

        <label htmlFor="preferred-channel">Voorkeurszender</label>
        <div className="select-wrap">
          <select
            id="preferred-channel"
            className="field field-select"
            value={preferredChannel}
            onChange={(event) => {
              const value = event.target.value;
              setPreferredChannel(isPreferredChannelKey(value) ? value : "");
              setError(null);
            }}
            required
            disabled={busy}
          >
            <option value="">Kies een zender</option>
            {CHANNEL_OPTIONS.map((channel) => (
              <option key={channel.key} value={channel.key}>
                {channel.name}
              </option>
            ))}
          </select>
        </div>

        {error && <p className="error">{error}</p>}

        <div className="row">
          <button
            className="primary"
            type="submit"
            disabled={busy || !postcode.trim() || !preferredChannel}
          >
            {busy ? "Bezig…" : "Opslaan"}
          </button>
          {mode === "edit" && (
            <button
              className="secondary"
              type="button"
              disabled={busy}
              onClick={onDone}
            >
              Annuleren
            </button>
          )}
        </div>
      </form>
    </main>
  );
}

/**
 * Hands the entire authentication flow to Clerk — sign-in and sign-up both, mounted here rather
 * than linked out to the Account Portal (see pageUrl). Every redirect target is this page with the
 * pairing code intact, so Clerk always returns the viewer to the pairing they were confirming.
 *
 * No heading of our own: Clerk's card brings its own title, and stacking a second one above it just
 * repeats the same sentence twice.
 */
function AuthStep() {
  const afterAuth = pageUrl();

  return (
    <main className="clerk-mount">
      {modeFromUrl() === "signup" ? (
        <SignUp
          routing="virtual"
          signInUrl={pageUrl()}
          fallbackRedirectUrl={afterAuth}
          signInFallbackRedirectUrl={afterAuth}
        />
      ) : (
        <SignIn
          routing="virtual"
          signUpUrl={pageUrl("signup")}
          fallbackRedirectUrl={afterAuth}
          signUpFallbackRedirectUrl={afterAuth}
        />
      )}
    </main>
  );
}

/** Shown when the page was opened without a code — re-enters through the URL so a refresh keeps it. */
function CodeForm() {
  const [value, setValue] = useState("");

  return (
    <main className="card">
      <h1>TV aanmelden</h1>
      <p>Vul de code in die op je tv staat.</p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const code = value.trim();
          if (code) {
            window.location.search = `?code=${encodeURIComponent(code)}`;
          }
        }}
      >
        <input
          id="code"
          name="code"
          className="field"
          aria-label="Code op je tv"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="XXXX-XXXX"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
        />
        <button className="primary" type="submit" disabled={!value.trim()}>
          Doorgaan
        </button>
      </form>
    </main>
  );
}

/**
 * The confirmation step, and the reason this page shows a device name rather than just a code:
 * naming the television is what lets a viewer notice that the code someone read out to them over
 * the phone is not their own TV. Approving is a deliberate act, and denying is offered next to it.
 */
function ConfirmStep({ userCode }: { userCode: string }) {
  const { getToken } = useAuth();
  const { user } = useUser();
  const [pairing, setPairing] = useState<Pairing | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const token = await getToken();
        if (!token) {
          throw new ApiError(0, "Geen geldige sessie.");
        }
        const found = await getPairing(userCode, token);
        if (!cancelled) {
          setPairing(found);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        const message =
          error instanceof ApiError && error.isUnknownCode
            ? "Deze code werkt niet meer. Vraag een nieuwe code aan op je tv."
            : error instanceof ApiError
              ? error.message
              : "Er ging iets mis.";
        setOutcome({ kind: "error", message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken, userCode]);

  const decide = useCallback(
    async (action: "approve" | "deny") => {
      setBusy(true);
      try {
        const token = await getToken();
        if (!token) {
          throw new ApiError(0, "Geen geldige sessie.");
        }

        if (action === "approve") {
          const result = await approvePairing(userCode, token);
          setOutcome({
            kind: "approved",
            deviceName: result?.deviceName ?? null,
          });
        } else {
          await denyPairing(userCode, token);
          setOutcome({ kind: "denied" });
        }
      } catch (error) {
        setOutcome({
          kind: "error",
          message:
            error instanceof ApiError ? error.message : "Er ging iets mis.",
        });
      } finally {
        setBusy(false);
      }
    },
    [getToken, userCode],
  );

  if (outcome) {
    return <Result outcome={outcome} />;
  }

  if (!pairing) {
    return <Card busy note="Je code wordt gecontroleerd…" />;
  }

  return (
    <main className="card">
      <h1>TV aanmelden</h1>
      <p className="code">{pairing.userCode}</p>
      <p>
        Wil je <strong>{pairing.deviceName ?? "deze tv"}</strong> aanmelden
        {user?.primaryEmailAddress?.emailAddress ? (
          <>
            {" "}
            als <strong>{user.primaryEmailAddress.emailAddress}</strong>?
          </>
        ) : (
          "?"
        )}
      </p>
      <div className="row">
        <button
          className="primary"
          type="button"
          disabled={busy}
          onClick={() => void decide("approve")}
        >
          {busy ? "Bezig…" : "Aanmelden"}
        </button>
        <button
          className="secondary"
          type="button"
          disabled={busy}
          onClick={() => void decide("deny")}
        >
          Dit was ik niet
        </button>
      </div>
    </main>
  );
}

/**
 * Every settled state, under the page's single heading. Unsuccessful outcomes also get a status
 * dot rather than their own heading — one screen, one title.
 */
function Result({ outcome }: { outcome: Outcome }) {
  const ok = outcome.kind === "approved";

  return (
    <main className="card">
      <h1>TV aanmelden</h1>
      <p className="lead">
        {ok &&
          `${outcome.deviceName ?? "Je tv"} is aangemeld. Je kunt dit tabblad sluiten.`}
        {outcome.kind === "denied" && "De aanmelding is geweigerd."}
        {outcome.kind === "error" && outcome.message}
      </p>
      {!ok && <span className="badge" aria-hidden="true" />}
    </main>
  );
}

/** Transient states: the heading stays put, only the line underneath changes. */
function Card({ note, busy }: { note: string; busy?: boolean }) {
  return (
    <main className="card">
      <h1>TV aanmelden</h1>
      <p>{note}</p>
      {busy && <div className="spinner" role="status" aria-label="Bezig" />}
    </main>
  );
}
